import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Upload,
  Button,
  Row,
  Col,
  Select,
  Alert,
  Space,
  Tooltip,
  message,
} from "antd";
import {
  UploadOutlined,
  EyeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import MarketPlaceService from "../../../utils/api/marketplace";
import CategorySelectorModal from "../supplier/CategorySelector";

const { Option } = Select;

const REQUIRED_FIELDS_FOR_UPLOAD = [
  { key: "name", label: "Nombre del Producto" },
  { key: "sku", label: "SKU" },
  { key: "price", label: "Precio de Costo" },
  { key: "stock", label: "Stock Disponible" },
  { key: "weight_kg", label: "Peso (kg)" },
  { key: "height_cm", label: "Alto (cm)" },
  { key: "width_cm", label: "Ancho (cm)" },
  { key: "length_cm", label: "Largo (cm)" },
  { key: "category", label: "Categoría Principal" },
  { key: "subcategory", label: "Subcategoría" },
  { key: "description", label: "Descripción del Producto" },
];

const NewProduct = ({ open, setOpen, onSubmit, vendor }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [formData, setFormData] = useState(null);
  const [openC, setOpenC] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [hasVisualized, setHasVisualized] = useState(false);
  const [priceTooltipOpen, setPriceTooltipOpen] = useState(false);

  useEffect(() => {
    if (open) {
      loadAllCategories();
      setHasVisualized(false);
      setPriceTooltipOpen(false);
    }
  }, [open]);

  const loadAllCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await MarketPlaceService.getCategories();

      let categoriesData = [];

      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.categories)
      ) {
        categoriesData = response.data.categories;
      } else if (response.data && Array.isArray(response.data)) {
        categoriesData = response.data;
      } else if (Array.isArray(response)) {
        categoriesData = response;
      }

      const validCategories = categoriesData
        .filter((cat) => cat && cat.name && cat.slug)
        .map((cat) => ({
          id: cat.id,
          name: cat.name,
          slug: cat.slug,
          parent_slug: cat.parent_slug || null,
          path: cat.path || cat.slug,
        }));

      setAllCategories(validCategories);

      const mainCategories = validCategories.filter((cat) => !cat.parent_slug);
      setCategories(mainCategories);
    } catch (error) {
      console.error("Error loading categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadSubcategories = async (categorySlug) => {
    try {
      const response = await MarketPlaceService.getSubcategoriesByCategory(
        categorySlug
      );
      let subcategoriesData = [];

      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.categories)
      ) {
        subcategoriesData = response.data.categories;
      } else if (response.data && Array.isArray(response.data)) {
        subcategoriesData = response.data;
      } else if (Array.isArray(response)) {
        subcategoriesData = response;
      }

      const filteredSubcategories = subcategoriesData.filter(
        (subcat) =>
          subcat.parent_slug === categorySlug ||
          subcat.parent === categorySlug ||
          (subcat.parent && subcat.parent.slug === categorySlug)
      );

      setSubcategories(filteredSubcategories);

      if (
        filteredSubcategories.length === 1 &&
        !form.getFieldValue("subcategory")
      ) {
        form.setFieldsValue({
          subcategory: filteredSubcategories[0].slug,
        });
      }
    } catch (error) {
      setSubcategories([]);
    }
  };

  const handleCategorySelect = (selection) => {
    setSelectedCategory(selection.categoryChain);

    const categoryNames = selection.categoryChain.map((slug) => {
      const category = allCategories.find((cat) => cat.slug === slug);
      return category ? category.name : slug;
    });

    form.setFieldsValue({
      category: categoryNames[0],
      subcategory: categoryNames[categoryNames.length - 1],
    });

    const lastSlug =
      selection.categoryChain[selection.categoryChain.length - 1];
    if (lastSlug) {
      loadSubcategories(lastSlug);
    }

    setOpenC(false);
  };

  const handleCategoryChange = (value) => {
    if (value) {
      loadSubcategories(value);
      form.setFieldsValue({
        subcategory: undefined,
      });
    } else {
      setSubcategories([]);
      form.setFieldsValue({
        subcategory: undefined,
      });
    }
  };

  const validateNumber = (rule, value) => {
    if (value === undefined || value === null || value === "") {
      return Promise.reject("Este campo es requerido");
    }

    if (!/^\d*\.?\d*$/.test(value)) {
      return Promise.reject("Solo se permiten números y punto decimal");
    }

    const numericValue = parseFloat(value);

    if (isNaN(numericValue) || numericValue < 0) {
      return Promise.reject("Ingresa un número válido mayor o igual a 0");
    }

    return Promise.resolve();
  };

  const validatePrice = (rule, value) => {
    if (value === undefined || value === null || value === "") {
      return Promise.reject("El precio es requerido");
    }

    if (!/^\d*\.?\d*$/.test(value)) {
      return Promise.reject(
        "Solo se permiten números y punto decimal para el precio"
      );
    }

    const numericValue = parseFloat(value);

    if (isNaN(numericValue) || numericValue < 0) {
      return Promise.reject("Ingresa un precio válido mayor o igual a 0");
    }

    const decimalPart = value.split(".")[1];
    if (decimalPart && decimalPart.length > 2) {
      return Promise.reject("El precio no puede tener más de 2 decimales");
    }

    return Promise.resolve();
  };

  const validateStock = (rule, value) => {
    if (value === undefined || value === null || value === "") {
      return Promise.reject("El stock es requerido");
    }

    if (!/^\d+$/.test(value)) {
      return Promise.reject("El stock debe ser un número entero sin decimales");
    }

    const numericValue = parseInt(value);

    if (isNaN(numericValue) || numericValue < 0) {
      return Promise.reject("Ingresa una cantidad válida mayor o igual a 0");
    }

    return Promise.resolve();
  };

  const handleNumericInput = (e, fieldName) => {
    const value = e.target.value;

    const filteredValue = value.replace(/[^\d.]/g, "");

    const parts = filteredValue.split(".");
    if (parts.length > 2) {
      return;
    }

    if (fieldName === "stock" && filteredValue.includes(".")) {
      return;
    }

    form.setFieldsValue({
      [fieldName]: filteredValue,
    });
  };

  const handlePriceInputClick = () => {
    setPriceTooltipOpen(true);
  };

  const handlePriceInputBlur = () => {
    setTimeout(() => {
      setPriceTooltipOpen(false);
    }, 200);
  };

  const handleVisualize = () => {
    setLoading(true);
    form
      .validateFields()
      .then((values) => {
        if (!values.sku) values.sku = "N/A";

        const price = values.price ? parseFloat(values.price) : 0;
        const stock = values.stock ? parseInt(values.stock) : 0;
        const weightKg = values.weight_kg ? parseFloat(values.weight_kg) : 0;
        const heightCm = values.height_cm ? parseFloat(values.height_cm) : 0;
        const widthCm = values.width_cm ? parseFloat(values.width_cm) : 0;
        const lengthCm = values.length_cm ? parseFloat(values.length_cm) : 0;
        setFormData({
          ...values,
          price: price,
          stock: stock,
          weight_kg: weightKg,
          height_cm: heightCm,
          width_cm: widthCm,
          length_cm: lengthCm,
          category: selectedCategory[0],
          subcategory: selectedCategory[selectedCategory.length - 1],
        });
        setIsConfirming(true);
        setHasVisualized(true);
        setLoading(false);
      })
      .catch((info) => {
        console.error("Validation Failed:", info);
        setLoading(false);
      });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const productWithArchive = {
        ...formData,
        archive: true,
      };
      await onSubmit(productWithArchive);
      message.success(
        "Producto creado exitosamente. Quedará pendiente de aprobación por el administrador."
      );
    } catch (error) {
      message.error("Error al crear el producto");
    } finally {
      setLoading(false);
      form.resetFields();
      setIsConfirming(false);
      setOpen(false);
      setSelectedCategory([]);
      setSubcategories([]);
      setHasVisualized(false);
      setPriceTooltipOpen(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    form.resetFields();
    setIsConfirming(false);
    setSelectedCategory([]);
    setSubcategories([]);
    setHasVisualized(false);
    setPriceTooltipOpen(false);
  };

  const handleBackToEdit = () => {
    setIsConfirming(false);
  };

  const handlePreview = async (file) => {
    if (!file.thumbUrl && file.originFileObj) {
      file.thumbUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file.originFileObj);
      });
    }
    return file.thumbUrl;
  };

  const getMissingRequiredFieldsForUpload = () => {
    const values = form.getFieldsValue(
      REQUIRED_FIELDS_FOR_UPLOAD.map((field) => field.key)
    );

    return REQUIRED_FIELDS_FOR_UPLOAD.filter((field) => {
      const value = values[field.key];
      return value === undefined || value === null || String(value).trim() === "";
    }).map((field) => field.label);
  };

  const warnMissingRequiredFieldsForUpload = (missingFields) => {
    if (missingFields.length === 0) return;

    message.warning(
      `Completa los campos obligatorios antes de subir imágenes: ${missingFields.join(
        ", "
      )}`
    );
  };

  const handleBeforeUpload = () => {
    const missingFields = getMissingRequiredFieldsForUpload();

    if (missingFields.length > 0) {
      form
        .validateFields(REQUIRED_FIELDS_FOR_UPLOAD.map((field) => field.key))
        .catch(() => null);
      warnMissingRequiredFieldsForUpload(missingFields);
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  return (
    <>
      <Modal
        title="Añadir Nuevo Producto"
        open={open}
        onCancel={handleCancel}
        footer={null}
        width={800}
      >
        <div
          className={`modal-container ${
            isConfirming ? "slide-left is-confirming" : "slide-right"
          }`}
        >
          <div className="form-section">
            <Form form={form} layout="vertical" name="new_product_form">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="Nombre del Producto"
                    rules={[
                      { required: true, message: "Este campo es requerido" },
                    ]}
                  >
                    <Input placeholder="Nombre del producto" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="sku"
                    label="SKU"
                    rules={[
                      { required: true, message: "Este campo es requerido" },
                    ]}
                  >
                    <Input placeholder="SKU" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="price"
                    label={
                      <Space>
                        Precio de Costo
                        <Tooltip
                          title="El precio debe incluir el IVA (Impuesto al Valor Agregado). Asegúrate de que el monto total ya contemple este impuesto."
                          open={priceTooltipOpen}
                          onOpenChange={(visible) => {
                            if (!visible) {
                              setPriceTooltipOpen(false);
                            }
                          }}
                        >
                          <InfoCircleOutlined
                            style={{ color: "#1890ff", cursor: "pointer" }}
                            onClick={() =>
                              setPriceTooltipOpen(!priceTooltipOpen)
                            }
                          />
                        </Tooltip>
                      </Space>
                    }
                    rules={[
                      { required: true, message: "El precio es requerido" },
                      { validator: validatePrice },
                    ]}
                  >
                    <Space.Compact style={{ width: "100%" }}>
                      <Button disabled style={{ cursor: "default" }}>
                        $
                      </Button>
                      <Input
                        style={{ width: "100%" }}
                        placeholder="Precio en MXN"
                        onChange={(e) => handleNumericInput(e, "price")}
                        onClick={handlePriceInputClick}
                        onBlur={handlePriceInputBlur}
                      />
                    </Space.Compact>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="stock"
                    label="Stock Disponible"
                    rules={[
                      { required: true, message: "El stock es requerido" },
                      { validator: validateStock },
                    ]}
                  >
                    <Input
                      style={{ width: "100%" }}
                      placeholder="Cantidad disponible"
                      onChange={(e) => handleNumericInput(e, "stock")}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="weight_kg"
                    label="Peso (kg)"
                    rules={[
                      { required: true, message: "El peso es requerido" },
                      { validator: validateNumber },
                    ]}
                  >
                    <Input
                      style={{ width: "100%" }}
                      placeholder="Peso en kg"
                      onChange={(e) => handleNumericInput(e, "weight_kg")}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="height_cm"
                    label="Alto (cm)"
                    rules={[
                      { required: true, message: "El alto es requerido" },
                      { validator: validateNumber },
                    ]}
                  >
                    <Input
                      style={{ width: "100%" }}
                      placeholder="Alto en cm"
                      onChange={(e) => handleNumericInput(e, "height_cm")}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="width_cm"
                    label="Ancho (cm)"
                    rules={[
                      { required: true, message: "El ancho es requerido" },
                      { validator: validateNumber },
                    ]}
                  >
                    <Input
                      style={{ width: "100%" }}
                      placeholder="Ancho en cm"
                      onChange={(e) => handleNumericInput(e, "width_cm")}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="length_cm"
                    label="Largo (cm)"
                    rules={[
                      { required: true, message: "El largo es requerido" },
                      { validator: validateNumber },
                    ]}
                  >
                    <Input
                      style={{ width: "100%" }}
                      placeholder="Largo en cm"
                      onChange={(e) => handleNumericInput(e, "length_cm")}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Alert
                message="Selección de Categoría"
                description="Usa el selector visual para elegir la categoría de tu producto"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item>
                    <Button
                      type="primary"
                      onClick={() => setOpenC(true)}
                      style={{ marginBottom: 16 }}
                      loading={loadingCategories}
                    >
                      Usar Selector Visual de Categorías
                    </Button>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="category"
                    label="Categoría Principal"
                    rules={[
                      {
                        required: true,
                        message:
                          "Selecciona una categoría usando el selector visual",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Selecciona categoría usando el botón de arriba"
                      loading={loadingCategories}
                      disabled={true}
                      allowClear
                      onChange={handleCategoryChange}
                    >
                      {categories.map((category) => (
                        <Option key={category.slug} value={category.slug}>
                          {category.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="subcategory"
                    label="Subcategoría"
                    rules={[
                      {
                        required: true,
                        message: "Por favor selecciona la subcategoría",
                      },
                    ]}
                  >
                    <Select
                      placeholder={
                        subcategories.length === 0
                          ? "Cargando subcategorías..."
                          : "Selecciona subcategoría"
                      }
                      disabled={
                        !form.getFieldValue("category") ||
                        subcategories.length === 0
                      }
                      loading={
                        subcategories.length === 0 &&
                        form.getFieldValue("category")
                      }
                    >
                      {subcategories.map((subcategory) => (
                        <Option key={subcategory.slug} value={subcategory.slug}>
                          {subcategory.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="description"
                label="Descripción del Producto"
                rules={[{ required: true, message: "Este campo es requerido" }]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Describe tu producto de manera detallada..."
                  showCount
                  maxLength={500}
                />
              </Form.Item>

              <Form.Item
                name="images"
                label="Imágenes del Producto"
                valuePropName="fileList"
                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                rules={[
                  { required: true, message: "Sube al menos una imagen" },
                ]}
                extra={
                  <div style={{ marginTop: 16, textAlign: "right" }}>
                    <Space>
                      <Button onClick={handleCancel}>Cancelar</Button>
                      <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={handleVisualize}
                        loading={loading}
                      >
                        Visualizar para avanzar
                      </Button>
                    </Space>
                  </div>
                }
              >
                <Upload
                  name="images"
                  listType="picture-card"
                  multiple
                  maxCount={2}
                  beforeUpload={handleBeforeUpload}
                  onPreview={handlePreview}
                  accept="image/*"
                >
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>Subir Imágenes</div>
                  </div>
                </Upload>
              </Form.Item>
            </Form>
          </div>

          <div className="confirm-section">
            <h3>Confirma la información del producto</h3>
            <div
              style={{
                background: "#f5f5f5",
                padding: "16px",
                borderRadius: "8px",
              }}
            >
              <p>
                <strong>Nombre:</strong> {formData?.name}
              </p>
              <p>
                <strong>SKU:</strong> {formData?.sku || "N/A"}
              </p>
              <p>
                <strong>Tu precio (incluye IVA):</strong> ${formData?.price} MXN
              </p>
              <p>
                <strong>Stock:</strong> {formData?.stock} unidades
              </p>
              <p>
                <strong>Peso:</strong> {formData?.weight_kg} kg
              </p>
              <p>
                <strong>Alto:</strong> {formData?.height_cm} cm
              </p>
              <p>
                <strong>Ancho:</strong> {formData?.width_cm} cm
              </p>
              <p>
                <strong>Largo:</strong> {formData?.length_cm} cm
              </p>
              <p>
                <strong>Categoría:</strong> {formData?.category}
              </p>
              <p>
                <strong>Subcategoría:</strong> {formData?.subcategory}
              </p>
              <p>
                <strong>Descripción:</strong> {formData?.description}
              </p>
            </div>
            <div style={{ marginTop: 20, textAlign: "right" }}>
              <Button onClick={handleBackToEdit} style={{ marginRight: 10 }}>
                Volver a Editar
              </Button>
              <Button
                type="primary"
                loading={loading}
                onClick={handleSubmit}
                disabled={!hasVisualized}
              >
                Confirmar producto
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <CategorySelectorModal
        open={openC}
        onClose={() => setOpenC(false)}
        onAccept={handleCategorySelect}
        categoriesData={allCategories}
      />
    </>
  );
};

export default NewProduct;
